import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { defineMessages } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { LearningSessionsApi } from 'src/api';
import { createSessionStore } from 'src/memlearning';
import { PresentSummary } from 'src/memlearning/presenter';
import MemLearning from '@memrise/memlearning';
import { convertTestResultFromMemLearning } from 'src/memlearning/conversion';
import { useDevTools } from 'src/hooks/useDevTools';
import { useLearningToggleSettings } from 'src/hooks';
import { useMutation } from '@tanstack/react-query';
import { FIVE_MINUTES } from 'src/constants/times.const';
import {
  CompactLearnableWithProgress,
  LearnableId,
  LearningEvent,
  SessionSourceType,
  SessionState,
  SessionType,
  WebSessionType,
} from 'src/api/learningSessions/types';
import { useExperienceContext } from 'src/context/experience';
import { isProduction } from 'src/utils/env/isProduction';
import { isCharacterTracingLanguageId } from 'src/utils/language/targetLanguageIds';
import { useCurrentLanguagePair } from 'src/hooks/useCurrentLanguagePair';
import {
  getPresentationCardData,
  getTemplateName,
  getTestCardData,
} from '../Common/learnableCards/utils';
import { useSessionLearnablesWithProgress } from './useSessionLearnablesWithProgress';
import { initialSessionState, learningSessionReducer } from './learningSessionReducer';
import {
  addBonusPointsToLearningEvents,
  batchSendLearningEvents,
  batchSendLearningEventsAtEndOfSession,
  convertSessionDataToSummary,
  generateLearningEvent,
  getNumCorrectAnswers,
  getPoints,
} from './utils';

const msgs = defineMessages({
  whoops: {
    id: 'Whoops.',
  },
  couldNotLoad: {
    id: 'learning_session_error_generic_header',
  },
});

const useSessionStore = (
  sessionType: WebSessionType,
  sessionSourceType: SessionSourceType,
  sessionSourceId: number,
  sessionId: string,
) => {
  const { experienceType } = useExperienceContext();
  // Always load learning toggles: character tracing applies to Chinese/Japanese sessions,
  // and difficult-words review also reads "show answer before test" from the same store.
  const { isFeatureShown, isLoading: isLoadingLearningToggles } = useLearningToggleSettings();
  const { languagePair } = useCurrentLanguagePair();
  const tracingEnabled =
    !isProduction() &&
    isCharacterTracingLanguageId(languagePair?.targetLanguageId) &&
    isFeatureShown('characterTracing');

  /* istanbul ignore next: please test in WEBSITE-7219 */
  const apiSessionSourceType =
    experienceType === '2025' && sessionSourceType === 'language_pair' && sessionType === 'learn'
      ? 'active_wordlist_for_language_pair'
      : sessionSourceType;
  const [learningSession, learningSessionDispatch] = useReducer(
    learningSessionReducer,
    initialSessionState,
  );
  const [learningEvents, setLearningEvents] = useState<
    Omit<LearningEvent, 'notDifficult' | 'ignored' | 'starred'>[]
  >([]);
  const [hasMarkedWordsAsDifficult, setHasMarkedWordsAsDifficult] = useState(false);
  const [ignoredItemsCount, setIgnoresItemsCount] = useState(0);
  const [testAnswerProcessed, setTestAnswerProcessed] = useState<boolean>(false);
  const [isDoneUpdatingProgress, setIsDoneUpdatingProgress] = useState<boolean>(false);
  const [isStoreSet, setIsStoreSet] = useState(false);
  const devToolState = useDevTools();
  const [userScenarioCompleted, setUserScenarioCompleted] = useState<boolean>(false);
  const analyticsSessionId = useMemo(() => uuidv4(), []);

  // The memlearning store is updated outside of React
  const storeRef = useRef<MemLearning.com.memrise.learning.session.SessionStore>(null);

  const { mutateAsync: batchSendLearningEventsRetry } = useMutation({
    mutationKey: ['batchSendLearningEvents'],
    mutationFn: ({
      unsentLearningEvents,
      progressPreferences,
    }: {
      unsentLearningEvents: Parameters<typeof batchSendLearningEvents>[0];
      progressPreferences: Parameters<typeof batchSendLearningEvents>[1];
    }) => {
      return batchSendLearningEvents(
        unsentLearningEvents,
        progressPreferences,
        sessionType,
        apiSessionSourceType,
        sessionSourceId,
        analyticsSessionId,
      );
    },
  });

  const { mutateAsync: batchSendLearningEventsAtEndOfSessionRetry } = useMutation({
    mutationKey: ['batchSendLearningEventsAtEndOfSession'],
    mutationFn: ({
      learningEvents,
      analyticsSessionId,
      sessionSourceType,
      sessionType,
      sessionSourceId,
      progressPreferences,
      sessionEndReason,
      sessionLearnableIds,
    }: {
      learningEvents: Parameters<typeof batchSendLearningEventsAtEndOfSession>[0];
      analyticsSessionId: Parameters<typeof batchSendLearningEventsAtEndOfSession>[1];
      sessionType: Parameters<typeof batchSendLearningEventsAtEndOfSession>[2];
      sessionSourceType: Parameters<typeof batchSendLearningEventsAtEndOfSession>[3];
      sessionSourceId: Parameters<typeof batchSendLearningEventsAtEndOfSession>[4];
      progressPreferences: Parameters<typeof batchSendLearningEventsAtEndOfSession>[5];
      sessionEndReason: Parameters<typeof batchSendLearningEventsAtEndOfSession>[6];
      sessionLearnableIds: Parameters<typeof batchSendLearningEventsAtEndOfSession>[7];
    }) =>
      batchSendLearningEventsAtEndOfSession(
        learningEvents,
        analyticsSessionId,
        sessionType,
        sessionSourceType,
        sessionSourceId,
        progressPreferences,
        sessionEndReason,
        sessionLearnableIds,
      ),
  });

  const [seenLearnablesWithUpdatedProgress, setSeenLearnablesWithUpdatedProgress] = useState<{
    [key: string]: CompactLearnableWithProgress;
  }>({});

  // Step 0. Load learnables from API
  const {
    learnablesWithProgress,
    learnablesWithProgressLookup,
    levelId,
    levelName,
    sessionSourceName,
    languagePairId,
    numItemsDueForReview,
    testSettings,
  } = useSessionLearnablesWithProgress({
    sessionType,
    sessionSourceType: apiSessionSourceType,
    sessionSourceId,
    learningSessionDispatch,
    sessionId,
  });

  const isDifficultWordsSession = sessionType === SessionType.DIFFICULT_WORDS;
  // Only difficult-words review honours this toggle; other session types ignore it.
  const showAnswerBeforeTest = isFeatureShown('showAnswerBeforeTest');

  const sessionLearnableIds = useMemo(
    () => learnablesWithProgress.map(lwp => lwp.learnable.id),
    [learnablesWithProgress],
  );

  /* istanbul ignore next: branch difficult to test from Memlearning */
  const onError = useCallback(async (error: Error) => {
    console.error(error);
    learningSessionDispatch({
      type: 'SET_ERROR',
      error: { header: msgs.whoops, description: msgs.couldNotLoad },
    });
  }, []);

  const updateSessionState = useCallback(
    (sessionState: Partial<SessionState>) => {
      const currentStore = storeRef.current;
      /* istanbul ignore next: should not happen in production */
      if (!currentStore) {
        console.error(`Current Store missing to update ${sessionState.kind}`);
        return;
      }

      switch (sessionState.kind) {
        case 'CardState': {
          // Partial<SessionState> is used here as we add the card.data in this
          // function, not in the presenter
          // We assert potentially undefined values to make the type system happy
          /* istanbul ignore next */
          if (typeof sessionState.card === 'undefined') throw new Error('no card on sessionState');
          /* istanbul ignore next */
          if (typeof sessionState.growthLevel === 'undefined')
            throw new Error('no growthLevel on sessionState');
          /* istanbul ignore next */
          if (typeof sessionState.sessionPercentComplete === 'undefined')
            throw new Error('no sessionPercentComplete on sessionState');
          /* istanbul ignore next */
          if (typeof sessionState.sessionItemCount === 'undefined')
            throw new Error('no sessionItemCount on sessionState');

          const newLearnableWithProgress =
            learnablesWithProgressLookup[sessionState.card.learnableId];

          /* istanbul ignore next: should always have a learnableWithProgress in CardState */
          if (!newLearnableWithProgress)
            throw new Error('Session is in CardState without a Learnable');

          switch (sessionState.card.kind) {
            case 'PresentationCard': {
              // Difficult-words review always sequences a leading presentation
              // card before each word's tests, and the memlearning sequencer
              // offers no way to omit it. When the learner has turned "show
              // answer before test" off we skip straight past it (rather than
              // presenting it) so they attempt the answer first; the meaning is
              // then revealed via the test's post-answer feedback.
              if (isDifficultWordsSession && !showAnswerBeforeTest) {
                currentStore.dispatch(
                  MemLearning.com.memrise.learning.session.ContinueSessionAction,
                );
                return;
              }

              const data = getPresentationCardData(
                newLearnableWithProgress.learnable,
                sessionState.card,
              );
              learningSessionDispatch({
                type: 'PRESENT_PRESENTATION_CARD',
                payload: {
                  card: {
                    ...sessionState.card,
                    // This is overriding the property from the conversion as there's no access to the store at that point
                    onContinue: () =>
                      currentStore.dispatch(
                        MemLearning.com.memrise.learning.session.ContinueSessionAction,
                      ),
                  },
                  growthLevel: sessionState.growthLevel,
                  sessionPercentComplete: sessionState.sessionPercentComplete,
                  sessionItemCount: sessionState.sessionItemCount,
                  data,
                  learnableWithProgress: newLearnableWithProgress,
                },
              });
              return;
            }
            case 'TestCard': {
              const timeCardShown = Date.now();
              // When "show answer before test" is off for difficult words, also
              // strip copy scaffolding so the answer is not revealed greyed-out
              // in the typing field / tapping hint row during the test itself.
              const disableCopy = isDifficultWordsSession && !showAnswerBeforeTest;
              const data = getTestCardData(
                newLearnableWithProgress.learnable,
                sessionState.card,
                timeCardShown,
                { tracingEnabled, disableCopy },
              );

              const card = sessionState.card;
              const currentStage = currentStore.state.stage;

              /* istanbul ignore else: not expected in production */
              if (
                currentStage instanceof MemLearning.com.memrise.learning.session.CardViewedStage &&
                currentStage.card
              ) {
                card.onTestAnswer = (answer: string) => {
                  currentStore.dispatch(
                    new MemLearning.com.memrise.learning.session.AnswerCardAction(
                      currentStage.card,
                      answer,
                    ),
                  );
                  // This code is converted from the old Memlearning sessionInteractor code
                  // It should likely be replaced by responding to TestAnsweredStage in future.
                  // https://github.com/Memrise/MemLearning/blob/main/src/commonMain/kotlin/com/memrise/learning/session/SessionInteractor.kt#L224
                  /* istanbul ignore next: unexpected as AnswerCardAction is synchronous */
                  if (
                    !(
                      currentStore.state.stage instanceof
                      MemLearning.com.memrise.learning.session.TestAnsweredStage
                    )
                  ) {
                    throw new Error('Test Result hasnt been provided.');
                  }
                  return convertTestResultFromMemLearning(currentStore.state.stage.testResult);
                };
              } else {
                console.error('TestCard state doesnt match CardViewedStage');
              }

              learningSessionDispatch({
                type: 'PRESENT_TEST_CARD',
                payload: {
                  card,
                  growthLevel: sessionState.growthLevel,
                  sessionPercentComplete: sessionState.sessionPercentComplete,
                  sessionItemCount: sessionState.sessionItemCount,
                  data,
                  learnableWithProgress: newLearnableWithProgress,
                },
              });
              return;
            }
            /* istanbul ignore next: type system not smart enough */
            default:
              throw new Error('you missed a switch case for sessionState.card.kind');
          }
        }
        case 'LoadingState': {
          learningSessionDispatch({ type: 'PRESENT_LOADING' });
          break;
        }
        /* istanbul ignore next: unimplemented session stages */
        default: {
          // There are lots of new states which should be used going forward but are not being used
          // for the first update away from the SessionInteractor.
          break;
        }
      }
    },
    [learnablesWithProgressLookup, isDifficultWordsSession, showAnswerBeforeTest, tracingEnabled],
  );

  const presentSummary: PresentSummary = useCallback(
    ({ endOfSessionPoints }) => {
      learningSessionDispatch({
        type: 'PRESENT_SUMMARY',
        summary: { endOfSessionPoints },
      });
    },
    [learningSessionDispatch],
  );

  /* Step 1. Create SessionStore when learnables with
   * progress have loaded
   */

  const sendStartSessionAnalytics = () => {
    if (sessionSourceType === 'language_pair') {
      void LearningSessionsApi.start({
        sessionId: analyticsSessionId,
        sessionType,
        sessionSourceType: apiSessionSourceType,
        languagePairId: sessionSourceId,
        allSessionLearnablesIdKeys: sessionLearnableIds,
      });
    }
  };
  const createStore = () => {
    // Wait for the difficult-review preference before starting a difficult-words
    // session so we never show the meaning-first presentation to a learner who
    // has chosen to attempt the answer before it is revealed.
    if (
      learnablesWithProgress.length < 1 ||
      !testSettings ||
      (isDifficultWordsSession && isLoadingLearningToggles)
    )
      return;

    const { store: memlearningStore, startSession } = createSessionStore({
      learnablesWithProgress,
      updateSessionState,
      presentSummary,
      sessionType,
      onMemlearningError: onError,
      testSettings,
      forceTestType: devToolState?.forceTestType,
    });
    storeRef.current = memlearningStore;
    setIsStoreSet(true);
    learningSessionDispatch({
      type: 'INITIALIZE_PROGRESS_PREFERENCES',
      learnablesWithProgress,
    });
    startSession();
    sendStartSessionAnalytics();
  };

  useEffect(() => {
    if (!isStoreSet) {
      createStore();
      return;
    }
    /* istanbul ignore if: dev tools only */
    if (devToolState?.forceTestType) {
      createStore();
    }
    // eslint-disable-next-line no-warning-comments
    // TODO: Check does it need createStore?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    learnablesWithProgress,
    sessionType,
    updateSessionState,
    testSettings,
    isStoreSet,
    presentSummary,
    onError,
    devToolState?.forceTestType,
    isLoadingLearningToggles,
    showAnswerBeforeTest,
  ]);

  const currentLearnableWithProgress =
    learningSession.stage === 'card' && learningSession.card
      ? learnablesWithProgressLookup[learningSession.card.card.learnableId]
      : null;

  /* Step 2.
   * This useEffect only updates when the responseState changes to `Processing`.
   * Calls onTestAnswered on the card and generates a learning event
   */
  useEffect(() => {
    if (
      learningSession.stage === 'card' &&
      learningSession.card.responseState === 'Processing' &&
      learningSession.card.data.kind === 'Test' &&
      learningSession.card.card.kind === 'TestCard' &&
      currentLearnableWithProgress !== null
    ) {
      const currentCard = learningSession.card.card;
      const templateName = getTemplateName(currentCard.testType.model);
      const { userAnswer } = learningSession.card;
      const testResult = currentCard.onTestAnswer(userAnswer);
      const { learnableProgress, isDifficult } = testResult;
      const { learnableId } = learnableProgress;

      /* istanbul ignore next: already mocked */
      if (isDifficult && (sessionType === SessionType.LEARN || sessionType === SessionType.MIXED)) {
        learningSessionDispatch({
          type: 'UPDATE_LEARNABLE_DIFFICULT',
          learnableId,
        });
      }

      const msTimeSpent = Math.min(
        Math.floor(Date.now() - learningSession.card.data.timeCardShown),
        FIVE_MINUTES,
      );

      const { growthLevel } = testResult.learnableProgress;
      learningSessionDispatch({
        type: 'UPDATE_GROWTH_LEVEL',
        growthLevel,
      });

      const answeredExtraInfo = {
        correctness: testResult.correctness,
        testId: uuidv4(),
        givenAnswer: userAnswer,
        oldLearnableWithProgress: currentLearnableWithProgress,
        templateName,
      };

      // Add this progress to the answered progress
      const newLearningEvent = generateLearningEvent({
        sessionSourceId,
        sessionSourceType,
        answeredExtraInfo,
        updatedProgress: testResult.learnableProgress,
        points: testResult.points,
        msTimeSpent,
      });

      // Set seen items for end of session summary
      setSeenLearnablesWithUpdatedProgress(learnables => ({
        ...learnables,
        [currentLearnableWithProgress.learnable.id]: {
          learnable: currentLearnableWithProgress.learnable,
          progress: testResult.learnableProgress,
        },
      }));
      setLearningEvents(learningEvents => learningEvents.concat(newLearningEvent));

      // Mark that the answer has been processed and the session
      // is ready to show the next card. Especially important for
      // making sure the last learning event in a session is
      // processed.
      setTestAnswerProcessed(true);
      learningSessionDispatch({
        type: 'SHOW_FEEDBACK',
        correctness: testResult.correctness,
      });
    }
  }, [
    analyticsSessionId,
    setSeenLearnablesWithUpdatedProgress,
    learningSession.card,
    learningSession.stage,
    seenLearnablesWithUpdatedProgress,
    setLearningEvents,
    setTestAnswerProcessed,
    currentLearnableWithProgress,
    learningSessionDispatch,
    sessionId,
    sessionType,
    sessionSourceType,
    sessionSourceId,
  ]);

  /*
   * Step 3. A callback is provided for the session to control the
   * transition to the next card. This has to be separate to step 2
   * because it needs to be called only once the user has a
   * chance to see the feedback. This might be called after a
   * delay timer has finished or the user clicks "Next".
   */
  const onTestContinue = useCallback(() => {
    /* istanbul ignore else: this function should only be called when in CardState  */
    if (
      learningSession.stage === 'card' &&
      testAnswerProcessed &&
      learningSession.card &&
      storeRef.current
    ) {
      storeRef.current.dispatch(MemLearning.com.memrise.learning.session.ContinueSessionAction);
      setTestAnswerProcessed(false);
    }
  }, [learningSession.card, learningSession.stage, testAnswerProcessed, setTestAnswerProcessed]);

  const endOfSessionPoints = learningSession.summary?.endOfSessionPoints;
  /*
   * Step 4. After the last learnableEvent has been added,
   * send the progress. We also log the end of session points
   * with the last learnable.
   */
  useEffect(() => {
    void (async function () {
      if (learningSession.stage !== 'summaryLoading') {
        return;
      }

      const hasLearningEventsToSend = learningEvents.length > 0;
      const hasFinishedProcessingLearningEvents = !testAnswerProcessed;

      /* istanbul ignore else: untested branch of code, please test */
      if (
        (hasLearningEventsToSend || hasMarkedWordsAsDifficult) &&
        hasFinishedProcessingLearningEvents
      ) {
        setIsDoneUpdatingProgress(false);
        try {
          /* Add end of session points to the last learning event before batch sending.
           * 1. First we make a shallow copy of the array using slice
           * 2. Then we spread the last learningEvent so we don't update an object
           * outside of the useState hook
           */

          const updatedLearningEvents = hasLearningEventsToSend
            ? addBonusPointsToLearningEvents(endOfSessionPoints || 0, learningEvents)
            : [];

          const learningSessionEndResponse = await batchSendLearningEventsAtEndOfSessionRetry({
            learningEvents: updatedLearningEvents,
            analyticsSessionId,
            sessionSourceType: apiSessionSourceType,
            sessionType,
            sessionSourceId,
            progressPreferences: learningSession.session.progressPreferences,
            sessionEndReason: 'completed',
            sessionLearnableIds,
          });

          setUserScenarioCompleted(
            learningSessionEndResponse.summaryStats?.userScenario?.percentComplete === 100,
          );

          const totalSessionPoints = getPoints(updatedLearningEvents);

          const summaryItems = convertSessionDataToSummary(
            seenLearnablesWithUpdatedProgress,
            learnablesWithProgressLookup,
          );
          learningSessionDispatch({
            type: 'SET_END_OF_SESSION_DATA',
            endOfSessionData: {
              nextSession: learningSessionEndResponse.nextSession,
              summaryItems,
              totalSessionPoints,
              userPath: learningSessionEndResponse?.summaryStats?.userPath || null,
              userScenario: learningSessionEndResponse?.summaryStats?.userScenario || null,
              dailyNewWords: learningSessionEndResponse.dailyNewWords,
            },
          });
        } catch (e) {
          /* istanbul ignore next */
          await onError(e as Error);
        }
        setIsDoneUpdatingProgress(true);
      } else if (!hasLearningEventsToSend) {
        const endOfSessionResponse = await LearningSessionsApi.end({
          sessionId: analyticsSessionId,
          sessionSourceId,
          sessionType,
          sessionSourceType: apiSessionSourceType,
          sessionPoints: 0,
          endReason: 'completed',
          allSessionLearnablesIdKeys: sessionLearnableIds,
        });

        const { summaryStats, nextSession, dailyNewWords } = endOfSessionResponse;
        learningSessionDispatch({
          type: 'SET_END_OF_SESSION_DATA',
          endOfSessionData: {
            nextSession,
            summaryItems: [],
            totalSessionPoints: 0,
            userPath: summaryStats?.userPath || null,
            userScenario: summaryStats?.userScenario || null,
            dailyNewWords,
          },
        });

        setIsDoneUpdatingProgress(true);
      }
    })();
    // eslint-disable-next-line no-warning-comments
    // TODO: check does it need hasMarkedWordsAsDifficult
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    learningEvents,
    testAnswerProcessed,
    seenLearnablesWithUpdatedProgress,
    sessionType,
    levelId,
    sessionId,
    learningSession.session.progressPreferences,
    learningSession.stage,
    endOfSessionPoints,
    sessionSourceId,
    sessionSourceType,
    onError,
    batchSendLearningEventsAtEndOfSessionRetry,
    setUserScenarioCompleted,
  ]);

  /**
   * Sends the current learning events e.g. for exiting a session before
   * reaching the end
   */
  const sendCurrentLearningProgress = async () => {
    const hasAlreadySentEvents =
      learningSession.stage === 'summaryLoading' || learningSession.stage === 'summary';

    if (hasAlreadySentEvents) {
      return;
    }

    const hasLearningEventsToSend = learningEvents.length > 0;

    if (hasLearningEventsToSend) {
      const unsentLearningEvents = [...learningEvents];

      await batchSendLearningEventsRetry({
        unsentLearningEvents,
        progressPreferences: learningSession.session.progressPreferences,
      });

      // once the learning events have been sent, we reset state to avoid
      // double counting points for the same learning event
      setLearningEvents([]);
    }

    await LearningSessionsApi.end({
      sessionId: analyticsSessionId,
      sessionSourceId,
      sessionType,
      sessionSourceType: apiSessionSourceType,
      sessionPoints: 0,
      endReason: 'exited',
      allSessionLearnablesIdKeys: sessionLearnableIds,
    });
  };

  // Derive number of correct answers and points from the learning events
  const numCorrectAnswers = getNumCorrectAnswers(learningEvents);
  const numIncorrectAnswers = learningEvents.length - numCorrectAnswers;
  const points = getPoints(learningEvents);

  const learnablesWithProgressLength = learnablesWithProgress.length;

  const currentLearnableProgressPreferences = currentLearnableWithProgress
    ? learningSession.session.progressPreferences[currentLearnableWithProgress.learnable.id]
    : undefined;

  const onItemIgnored = () => {
    const currentStore = storeRef.current;
    /* istanbul ignore next: shouldn't happen in production */
    if (!(
      currentStore &&
      (currentStore.state.stage instanceof
        MemLearning.com.memrise.learning.session.CardViewedStage ||
        currentStore.state.stage instanceof
          MemLearning.com.memrise.learning.session.TestAnsweredStage ||
        currentStore.state.stage instanceof
          MemLearning.com.memrise.learning.session.CardSkippedStage ||
        currentStore.state.stage instanceof
          MemLearning.com.memrise.learning.session.MarkAsKnownStage ||
        currentStore.state.stage instanceof
          MemLearning.com.memrise.learning.session.SetDifficultyStage)
    )) {
      // It is possible to be in this stage while ignoring a learnable due to awaited ignore async api call
      if (
        currentStore &&
        currentStore.state.stage instanceof MemLearning.com.memrise.learning.session.SummaryStage
      )
        return;
      // The impact of this is that the item will not be removed from the
      // current learning session.
      console.error(
        `Current Store missing or Card in incorrect stage ${currentStore?.state.stage.constructor.name}. Stage: ${currentStore?.state.stage}`,
      );
      return;
    }

    /* istanbul ignore next: hard to test */
    if (currentLearnableWithProgress === null) return;

    const stageBeforeIgnore = currentStore.state.stage;
    const card = stageBeforeIgnore.card;
    // Ignore item
    currentStore.dispatch(
      new MemLearning.com.memrise.learning.session.MarkAsKnownAction(
        card as MemLearning.com.memrise.learning.session.LearnableCard,
      ),
    ); // TO-DO - fix casting

    setHasMarkedWordsAsDifficult(true);
    setIgnoresItemsCount(prevCount => prevCount + 1);
    setSeenLearnablesWithUpdatedProgress(learnables => ({
      ...learnables,
      [currentLearnableWithProgress.learnable.id]: {
        learnable: currentLearnableWithProgress.learnable,
        progress: {
          ...currentLearnableWithProgress.progress,
          ignored: true,
        },
      },
    }));

    currentStore.dispatch(MemLearning.com.memrise.learning.session.ContinueSessionAction);
  };

  // Moves past the current test without recording an answer
  const onItemSkipped = () => {
    const currentStore = storeRef.current;
    if (
      !currentStore ||
      !(
        currentStore.state.stage instanceof MemLearning.com.memrise.learning.session.CardViewedStage
      )
    ) {
      return;
    }

    const { card } = currentStore.state.stage;
    if (learningSession.stage !== 'card' || learningSession.card.card.kind !== 'TestCard') return;

    currentStore.dispatch(
      new MemLearning.com.memrise.learning.session.SkipCardAction(
        card as MemLearning.com.memrise.learning.session.LearnableCard,
      ),
    );
    currentStore.dispatch(MemLearning.com.memrise.learning.session.ContinueSessionAction);
  };

  const onItemDifficultyChanged = (difficult: boolean) => {
    const currentStore = storeRef.current;
    /* istanbul ignore next: shouldn't happen in production */
    if (!(
      currentStore &&
      (currentStore.state.stage instanceof
        MemLearning.com.memrise.learning.session.CardViewedStage ||
        currentStore.state.stage instanceof
          MemLearning.com.memrise.learning.session.TestAnsweredStage ||
        currentStore.state.stage instanceof
          MemLearning.com.memrise.learning.session.CardSkippedStage ||
        currentStore.state.stage instanceof
          MemLearning.com.memrise.learning.session.MarkAsKnownStage ||
        currentStore.state.stage instanceof
          MemLearning.com.memrise.learning.session.SetDifficultyStage)
    )) {
      console.error(
        `Current Store missing or Card in incorrect stage ${currentStore?.state.stage.constructor.name}. Stage: ${currentStore?.state.stage}`,
      );
      return;
    }

    /* istanbul ignore next: hard to test */
    if (currentLearnableWithProgress === null) return;

    const stageBeforeStarred = currentStore.state.stage;
    const card = stageBeforeStarred.card;

    currentStore.dispatch(
      new MemLearning.com.memrise.learning.session.SetLearnableDifficultyAction(
        card as MemLearning.com.memrise.learning.session.LearnableCard,
        difficult,
      ), // TO-DO - fix casting
    );

    setHasMarkedWordsAsDifficult(true);

    setSeenLearnablesWithUpdatedProgress(learnables => ({
      ...learnables,
      [currentLearnableWithProgress.learnable.id]: {
        learnable: currentLearnableWithProgress.learnable,
        progress: {
          ...currentLearnableWithProgress.progress,
          notDifficult: !difficult,
          starred: difficult,
        },
      },
    }));

    // continue to next card when unstarring word during Difficult Words session
    /* istanbul ignore else: empty else branch */
    if (!difficult && sessionType === SessionType.DIFFICULT_WORDS) {
      currentStore.dispatch(MemLearning.com.memrise.learning.session.ContinueSessionAction);
    }
  };

  // To determine the first time we see a word in a session or not
  const currentLearnableAttempts = learningEvents.find(
    el => el.learnableId === currentLearnableWithProgress?.learnable?.id,
  )?.attempts;

  const markPreviousAnswerCorrect = useCallback(
    (learnableId: LearnableId) => {
      const currentStore = storeRef.current;
      if (!currentStore) {
        return;
      }

      // Find the most recent learning event for this learnable
      const recentEventIndex = learningEvents.findIndex(event => event.learnableId === learnableId);

      if (recentEventIndex === -1) {
        return;
      }

      const eventToCorrect = learningEvents[recentEventIndex];

      // Check if frontend event was already corrected but still dispatch MemLearning action
      const frontendAlreadyCorrected = eventToCorrect.score === 1;

      // Only update the frontend learning event if it wasn't already corrected
      if (!frontendAlreadyCorrected) {
        // Update the learning event to mark it as correct
        const correctedEvent = {
          ...eventToCorrect,
          score: 1, // Mark as correct
          points: Math.max(eventToCorrect.points, 1), // Ensure positive points
          // Update progress to reflect correctness
          correct: eventToCorrect.correct + 1,
          attempts: eventToCorrect.attempts, // Keep same attempts
          currentStreak: eventToCorrect.currentStreak + 1,
          totalStreak: eventToCorrect.totalStreak + 1,
        };

        // Update the learning events array
        const updatedLearningEvents = [...learningEvents];
        updatedLearningEvents[recentEventIndex] = correctedEvent;
        setLearningEvents(updatedLearningEvents);
      }

      // Use the new CorrectPreviousAnswerAction to tell MemLearning to:
      // 1. Update its internal history to mark this learnable as correct
      // 2. Remove any scheduled repeat tests for this learnable
      // 3. Treat the learnable as if it was answered correctly from the start
      currentStore.dispatch(
        new MemLearning.com.memrise.learning.session.CorrectPreviousAnswerAction(
          learnableId.toString(),
        ),
      );
    },
    [learningEvents, setLearningEvents, storeRef],
  );

  return {
    // Distinct from the getInitialProps `sessionId` that feeds Snowplow's learning_session_id.
    analyticsSessionId,
    currentLearnableWithProgress,
    currentLearnableAttempts,
    numCorrectAnswers,
    numIncorrectAnswers,
    points,
    levelName,
    sessionSourceName,
    onTestContinue,
    isDoneUpdatingProgress,
    // LevelId and learnablesWithProgressLength are only used for tracking and should be removed as part of WEBSITE-1964
    levelId,
    languagePairId,
    learnablesWithProgress,
    learnablesWithProgressLength,
    sendCurrentLearningProgress,
    numItemsDueForReview,
    currentLearnableProgressPreferences,
    learningSession,
    learningSessionDispatch,
    onItemIgnored,
    onItemSkipped,
    onItemDifficultyChanged,
    userScenarioCompleted,
    ignoredItemsCount,
    markPreviousAnswerCorrect,
  };
};

export default useSessionStore;
